import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { v4 as uuidv4 } from 'uuid';
import { CohortNotifications } from 'App.types';
import { SiteResponse } from './useSiteFiltering';
import { useDowntimeConflictValidation } from './useDowntimeConflictValidation';

export interface SelectedSite {
  captain_id: string;
  learn_url?: string;
  fleet_name?: string;
  aws_account?: string;
  notifications?: CohortNotifications;
}

export interface FormErrors {
  cohortName?: string;
  upgradeDate?: string;
  sites?: string;
}

interface UseCohortCreationOptions {
  minDaysFromToday?: number;
}

interface ValidationResult {
  isValid: boolean;
  errors: FormErrors;
  cohortData?: {
    id: string;
    name: string;
    downtime_date: string;
    enable_notifications: boolean;
  };
  siteIds?: string[];
}

const getUserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const useCohortCreation = ({ minDaysFromToday = 15 }: UseCohortCreationOptions = {}) => {
  const { t } = useTranslation();
  const { getConflictingSites } = useDowntimeConflictValidation();

  const [cohortName, setCohortName] = useState('');
  const [upgradeDate, setUpgradeDate] = useState<Date | null>(null);
  const [enableNotifications, setEnableNotifications] = useState(true);
  const [selectedSites, setSelectedSites] = useState<Map<string, SelectedSite>>(new Map());
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const { minDateString, suggestedDate } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const minDate = addDays(now, minDaysFromToday);
    const suggested = new Date(minDate);
    suggested.setHours(2, 0, 0, 0);

    const year = minDate.getFullYear();
    const month = String(minDate.getMonth() + 1).padStart(2, '0');
    const day = String(minDate.getDate()).padStart(2, '0');
    const hours = String(minDate.getHours()).padStart(2, '0');
    const minutes = String(minDate.getMinutes()).padStart(2, '0');

    return {
      minDateString: `${year}-${month}-${day}T${hours}:${minutes}`,
      suggestedDate: suggested,
    };
  }, [minDaysFromToday]);

  const selectedSitesArray = useMemo(() => Array.from(selectedSites.values()), [selectedSites]);

  const conflictingSites = useMemo(
    () => getConflictingSites(upgradeDate, Array.from(selectedSites.values())),
    [upgradeDate, selectedSites, getConflictingSites],
  );

  const handleCohortNameChange = useCallback(
    (value: string) => {
      setCohortName(value);
      if (formErrors.cohortName) {
        setFormErrors((prev) => ({ ...prev, cohortName: undefined }));
      }
    },
    [formErrors.cohortName],
  );

  const handleUpgradeDateChange = useCallback(
    (date: Date | null) => {
      setUpgradeDate(date);
      if (date && formErrors.upgradeDate) {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const minDate = addDays(now, minDaysFromToday);
        if (date >= minDate) {
          setFormErrors((prev) => ({ ...prev, upgradeDate: undefined }));
        }
      }
    },
    [formErrors.upgradeDate, minDaysFromToday],
  );

  const handleEnableNotificationsChange = useCallback((enabled: boolean) => {
    setEnableNotifications(enabled);
  }, []);

  const toggleSiteSelection = useCallback(
    (siteId: string, siteData?: SiteResponse) => {
      setSelectedSites((prev) => {
        const next = new Map(prev);
        if (next.has(siteId)) {
          next.delete(siteId);
        } else if (siteData) {
          next.set(siteId, {
            captain_id: siteData.captain_id,
            learn_url: siteData.learn_url,
            fleet_name: siteData.fleet_name,
            aws_account: siteData.aws_account,
            notifications: siteData.notifications,
          });
        }
        return next;
      });

      if (formErrors.sites) {
        setFormErrors((prev) => ({ ...prev, sites: undefined }));
      }
    },
    [formErrors.sites],
  );

  const validateAndPrepare = useCallback((): ValidationResult => {
    const errors: FormErrors = {};

    if (!cohortName.trim()) {
      errors.cohortName = t('cohorts.validation.nameRequired');
    }

    if (!upgradeDate) {
      errors.upgradeDate = t('cohorts.validation.upgradeDateRequired');
    } else {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const minDate = addDays(now, minDaysFromToday);
      if (upgradeDate < minDate) {
        errors.upgradeDate = t('cohorts.validation.upgradeDateTooSoon', {
          minDays: minDaysFromToday,
        });
      }
    }

    if (selectedSites.size === 0) {
      errors.sites = t('cohorts.validation.sitesRequired');
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return { isValid: false, errors };
    }

    if (!upgradeDate) {
      return {
        isValid: false,
        errors: { upgradeDate: t('cohorts.validation.upgradeDateRequired') },
      };
    }

    const userTimezone = getUserTimezone();
    const utcDate = fromZonedTime(upgradeDate, userTimezone);

    return {
      isValid: true,
      errors: {},
      cohortData: {
        id: uuidv4(),
        name: cohortName.trim(),
        downtime_date: utcDate.toISOString(),
        enable_notifications: enableNotifications,
      },
      siteIds: Array.from(selectedSites.keys()),
    };
  }, [cohortName, upgradeDate, enableNotifications, selectedSites, minDaysFromToday, t]);

  const resetForm = useCallback(() => {
    setCohortName('');
    setUpgradeDate(null);
    setEnableNotifications(true);
    setSelectedSites(new Map());
    setFormErrors({});
  }, []);

  const getUserTimezoneInfo = useCallback(() => {
    const timezone = getUserTimezone();
    const now = new Date();
    const timeString = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(now);
    return { timezone, timeString };
  }, []);

  return {
    cohortName,
    upgradeDate,
    enableNotifications,
    selectedSites,
    formErrors,
    minDateString,
    suggestedDate,
    selectedSitesArray,
    selectedSitesCount: selectedSites.size,
    conflictingSites,
    handleCohortNameChange,
    handleUpgradeDateChange,
    handleEnableNotificationsChange,
    toggleSiteSelection,
    resetForm,
    validateAndPrepare,
    getUserTimezoneInfo,
  };
};
